import React, { useState } from 'react';
import { HTML, Icon, Label } from 'src/components/Layout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Modal from 'src/components/Modal';
import Button from 'src/components/Button';
import {
  faCaretDown,
  faCaretUp,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';

import { colorTypes, getBrandingColor } from 'src/branding';
import { Input } from 'src/components/Input';
import { int, sumArrayByFunc } from 'src/utils/misc.utils';
import {
  Wrapper,
  FormDescription,
  AnimalRow,
  Group,
  GroupName,
  ActionButton,
  Dropdown,
  AnimalRowAndButtonsWrapper,
  GrpupNameAndFields,
  CommentHeader,
  FormFields,
  Header,
  CommonFieldsWrapper,
  CommentForAnimalRow,
} from './Components';
import { HoverRow, InputError } from '../Components';

export const getAnimalRowID = (row, args) => (row.club_no || '') + (row.mark_no || '') + (row.individual_no || '')
    + (row.ear_tag || '') + row.subcategory_name + (row.club_no && row.idx_in_club !== undefined && !args?.ignoreIdxInClub ? (`_idx_in_club_${row.idx_in_club}`) : '');

export const getAnimalRowsForGroup = (group, animalRows, disableGrouping) => {
  if (!Array.isArray(animalRows)) {
    return [];
  }
  const animals = [];

  for (const animalRow of animalRows) {
    // if not divided into a group or if grouping is disabled, just add the animal row
    // add so each animals gets each own
    if (((animalRow.return_options_groups || []).length === 0 && group.isDefaultGroup) || disableGrouping) {
      animals.push(animalRow);
      continue;
    }

    if (!Array.isArray(animalRow.return_options_groups)) {
      animalRow.return_options_groups = [];
    }
    // if divided, get a copy of each animal per relevant return_options group for that animal
    for (const returnGroup of animalRow.return_options_groups) {
      if (returnGroup.group_id === group.id) {
        animals.push({ ...animalRow, amount_for_row: returnGroup.amount_animals });
      }
    }
  }
  return animals;
};

/**
 * Allows the user to specify different options for the animals marked with either return meat(returkjøtt) or return skin(returhud/returskinn).
 * The animals are divided into groups, each group can have different options.
 * Each group gets is form fields that allows the user to change the options from the form-variable.
 * There is also some options that are common for all groups, that is also retrieved from the form-variable.
 *
 * The animals with returnskin/returmeat and which group they below to is stoed in animalRows
 * The groups and their options are stored in groupsData
 *
 * Only one instance of this component where you can select animals are supported
 */

export const ReturnForm = ({
  animalKind,
  animalRows,
  groupsData,
  commonData,
  description,
  comment,
  onMoveAnimalsToGroup,
  onChangeGroupComment,
  onChangeFormValue,
  onDeleteGroup,
  dontShowEmptyGroups,
  form,
  disableGrouping,
  commonFieldsKinds,
  // makes all inputs read-only, removves all action-buttons and make the return form look more like a receipt
  // also hides undivided animals
  isReceiptMode,
  header,
}) => {
  const [groupsCollapsed, setGroupsCollapsed] = useState(new Set());
  const [rowsCheckedForMoveByGroup, setRowsCheckedForMoveByGroup] = useState({});

  const [groupIdToConfirmDelete, setGroupIdToConfirmDelete] = useState(null);

  const getGroupById = (id) => groupsData.find((g) => g.id === id);

  const moveSelectedToGroup = ({ from, to, moveToNew }) => {
    let newGroupId = null;
    if (moveToNew) {
      newGroupId = Date.now();
    }
    const animalRowsForGroup = getAnimalRowsForGroup(getGroupById(from) || { isDefaultGroup: true }, animalRows, disableGrouping);
    const animalRowsForMove = animalRowsForGroup.filter((a, idx) => rowsCheckedForMoveByGroup[from].has(idx));
    let highestDivisionNo = 0;
    for (const g of groupsData) {
      if (g.divisionNo > highestDivisionNo) {
        highestDivisionNo = g.divisionNo;
      }
    }

    onMoveAnimalsToGroup({
      animal_rows_to_move: animalRowsForMove,
      moveFromGroupId: from,
      moveToGroupId: moveToNew ? newGroupId : to,
      newGroup: moveToNew ? { id: newGroupId, divisionNo: highestDivisionNo + 1 } : null,
    });
    setRowsCheckedForMoveByGroup({});
  };

  const changeCommentForGroup = (groupId, comment) => {
    onChangeGroupComment(groupId, comment);
  };

  const toggleGroupOpenClosed = (groupId) => {
    const copy = new Set(groupsCollapsed);
    if (!copy.has(groupId)) {
      copy.add(groupId);
    } else {
      copy.delete(groupId);
    }
    setGroupsCollapsed(copy);
  };

  const getGroupHeader = (g) => {
    if (g.customHeader) {
      return g.customHeader;
    }
    return g.isDefaultGroup ? 'Ufordelte dyr til oppdeling' : `Oppdeling ${g.divisionNo}`;
  };

  const changeFormValueInGroup = ({
    groupId, fieldId, value, uncheckFieldIds,
  }) => {
    if (isReceiptMode) {
      return;
    }
    onChangeFormValue({
      groupId, fieldId, value, uncheckFieldIds,
    });
  };

  const changeFormValueCommon = ({ fieldId, value, uncheckFieldIds }) => {
    if (isReceiptMode) {
      return;
    }
    onChangeFormValue({
      groupId: null, fieldId, value, uncheckFieldIds,
    });
  };

  const deleteGroup = (groupId) => {
    onDeleteGroup({ groupId });
    setGroupIdToConfirmDelete(null);
  };

  // animals without a return options group
  const undividedAnimals = animalRows
    .filter((a) => disableGrouping || (a.return_options_groups || []).length === 0);

  const moveBetweenGroupsEnabled = false;

  return (
    <Wrapper className="wrapper" isReceiptMode={isReceiptMode}>
      {form.isFetching
        || (!form.data && !form.error && <p>Laster...</p>)
        || (form.error && <p>Det oppsto en feil ved henting av skjemaet.</p>)
        || description && (
          <FormDescription>
            {description && <HTML>{description}</HTML>}
          </FormDescription>
        )}

      {header
        && <Header>{header}</Header>}

      {form.data
        && groupsData.map((g, idx) => ({ ...g, no: idx + 1 }))
          .concat(
            undividedAnimals.length > 0 && !isReceiptMode
              ? [{ isDefaultGroup: true, id: 'UNDIVIDED' }]
              : [],
          )
          .map((g, idx, arr) => (
            ((dontShowEmptyGroups && getAnimalRowsForGroup(g, animalRows, disableGrouping).length > 0) || !dontShowEmptyGroups)
            && (
              <Group className="group" isDefaultGroup={g.isDefaultGroup}>
                {!disableGrouping
              && (
                <GrpupNameAndFields>
                  <>
                    <GroupName
                      className="group-name"
                      disableHoverEffect={g.isDefaultGroup || isReceiptMode}
                      onClick={() => !g.isDefaultGroup && !isReceiptMode && toggleGroupOpenClosed(g.id)}
                    >
                      {getGroupHeader(g)}

                      {!g.isDefaultGroup && !isReceiptMode && (
                        <>
                          <FontAwesomeIcon
                            style={{
                              position: 'absolute',
                              right: '1em',
                              top: '1em',
                            }}
                            icon={
                              groupsCollapsed.has(g.id) ? faCaretUp : faCaretDown
                            }
                          />
                        </>
                      )}
                    </GroupName>
                    <div style={{
                      marginBottom: '1em',
                      marginLeft: '1.3rem',
                      marginTop: '-0.7em',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                    >
                      {!g.isDefaultGroup && !groupsCollapsed.has(g.id) && !isReceiptMode && (
                        <HoverRow
                          onClick={() => setGroupIdToConfirmDelete(g.id)}
                          style={{
                            fontSize: '0.75rem',
                            color: getBrandingColor(colorTypes.primary),
                          }}
                        >
                          <Icon
                            size="1.4em"
                            marginRight="0.4em"
                            icon={faTimes}
                          />
                          <Label textDecoration="underline" role="button">
                            Slett
                          </Label>
                        </HoverRow>
                      )}
                    </div>
                  </>

                  {!g.isDefaultGroup && !groupsCollapsed.has(g.id) && !g.hideFields && (
                    <>
                      <FormFields
                        fields={form.data.form_fields.per_division}
                        illegalCheckboxCombinations={
                        form.data['illegal-checkbox-combinations']
                      }
                        onFieldChange={changeFormValueInGroup}
                        groupId={g.id}
                        formData={g.formData}
                        isReceiptMode={isReceiptMode}
                      />
                    </>
                  )}
                </GrpupNameAndFields>
              )}

                {!groupsCollapsed.has(g.id) && (
                  <>
                    <AnimalRowAndButtonsWrapper className="AnimalRowAndButtonsWrapper">
                      {(rowsCheckedForMoveByGroup[g.id] || new Set()).size
                      > 0
                      && groupsData.length === 0 && (
                        <ActionButton
                          style={{
                            position: 'absolute',
                            top: '0.75rem',
                            right: '0.75rem',
                            zIndex: '2',
                          }}
                          onClick={() => {
                            moveSelectedToGroup({ from: 'UNDIVIDED', moveToNew: true });
                          }}
                        >
                          Velg oppdeling
                        </ActionButton>
                      )}

                      {(rowsCheckedForMoveByGroup[g.id] || new Set()).size
                      > 0
                      && groupsData.length > 0 && (
                        <Dropdown
                          label="Velg oppdeling å flytte til"
                          style={{
                            position: 'absolute',
                            top: '0.75rem',
                            right: '0.75rem',
                            zIndex: '2',
                          }}
                          onChange={(val) => {
                            if (val === 'CREATE_NEW_GROUP') {
                              moveSelectedToGroup({ from: g.id, moveToNew: true });
                            } else {
                              moveSelectedToGroup({ from: g.id, to: val });
                            }
                          }}
                          items={groupsData
                            .map((g, idx) => ({ ...g, no: idx + 1 }))
                            .filter(
                              (dropdownGroup) => g.id !== dropdownGroup.id,
                            )
                            .map((g, idx) => ({ label: `Oppdeling ${g.divisionNo}`, value: g.id }))
                            .concat([
                              {
                                label: 'Opprett ny oppdeling',
                                value: 'CREATE_NEW_GROUP',
                              },
                            ])}
                        />
                      )}
                      {
                      getAnimalRowsForGroup(g, animalRows, disableGrouping)
                        .map((a, rowIdx) => (
                          <AnimalRow
                            animal={a}
                            disabledFields={['count']}
                            animalKind={animalKind}
                            rowIdx={rowIdx}
                            groupId={g.id}
                            moveBetweenGroupsEnabled={moveBetweenGroupsEnabled || g.isDefaultGroup}
                            hideCheckbox={disableGrouping}
                            isChecked={(
                              (rowsCheckedForMoveByGroup[g.id] || new Set())
                            ).has(rowIdx)}
                            isReceiptMode={isReceiptMode}
                            onCheckChange={(checked) => {
                              let checkedForMoveCopy = {
                                ...rowsCheckedForMoveByGroup,
                              };

                              if (!checkedForMoveCopy[g.id]) {
                                checkedForMoveCopy[g.id] = new Set();
                              }

                              // we want to uncheck every one from other groups
                              // so that only animals from this group can be checked
                              checkedForMoveCopy = { [g.id]: checkedForMoveCopy[g.id] };

                              if (checked) {
                                checkedForMoveCopy[g.id].add(rowIdx);
                              } else {
                                checkedForMoveCopy[g.id].delete(rowIdx);
                              }

                              setRowsCheckedForMoveByGroup(checkedForMoveCopy);
                            }}
                          />
                        ))
}
                    </AnimalRowAndButtonsWrapper>
                  </>
                )}
              </Group>
            )
          ))}

      {form.data && (
        <CommonFieldsWrapper isReceiptMode={isReceiptMode} className="common-fields">
          <FormFields
            noLeftPadding
            fields={(commonFieldsKinds.includes('skin') ? form.data.form_fields['common-skin'] || [] : [])
              .concat((commonFieldsKinds.includes('meat') ? form.data.form_fields['common-meat'] || [] : []))}
            onFieldChange={changeFormValueCommon}
            formData={commonData}
            isReceiptMode={isReceiptMode}
          />

          { (!isReceiptMode || (isReceiptMode && comment)) && (
            <>
              <CommentHeader>Kommentar</CommentHeader>
              <CommentForAnimalRow
                comment={comment}
                showComment
                isReceiptMode={isReceiptMode}
                fullwidth
                center={false}
                onChangeComment={(comment) => changeCommentForGroup(comment)}
              />
            </>
          )}
        </CommonFieldsWrapper>
      )}

      {groupIdToConfirmDelete !== null && (
        <Modal>
          <Label size="1.15em" padding="1.5em">
            Er du sikker på å slette denne gruppen?
            <br />
            <br />
            Alle dyr i gruppen blir flyttet
            til "Ufordelte dyr til oppdeling".
          </Label>

          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              fontSize: '1.15em',
              justifyContent: 'center',
            }}
          >
            <Button
              secondary
              style={{
                padding: '12px 41px',
              }}
              onClick={() => setGroupIdToConfirmDelete(null)}
            >
              Nei
            </Button>
            <Button
              style={{
                marginLeft: '1em',
                padding: '12px 41px',
              }}
              warn
              onClick={() => { deleteGroup(groupIdToConfirmDelete); }}
            >
              Ja
            </Button>
          </div>
        </Modal>
      )}
    </Wrapper>
  );
};
